'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuthStore } from '@/store/auth-store';
import { useChatStore } from '@/store/chat-store';
import { useNotificationStore } from '@/store/notification-store';
import { BudgetAlertWatcher } from '@/components/providers/BudgetAlertWatcher';
import { logger } from '@/lib/logger';

// Forced-password-change overlay. Narrowly scoped (only fires when the logged-in
// user carries must_change_password and isn't already on the change-password or
// login screens) so it can't reintroduce the historic /login↔/chat bounce loop.
// This is the single enforcement point that covers EVERY page (the app doesn't
// gate routes through ProtectedRoute consistently).
function ForcedPasswordChangeGuard() {
  const router = useRouter();
  const pathname = usePathname();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const p = pathname || '';
    if ((user as any).must_change_password && p !== '/change-password' && !p.startsWith('/login')) {
      router.replace('/change-password');
    }
  }, [isAuthenticated, user, pathname, router]);
  return null;
}

// 2026-05-21 — All client-side auth redirects intentionally removed.
//
// Rationale (after the 2026-05-20 re-audit kept showing every protected
// route bouncing to /chat):
//
//   The middleware (src/middleware.ts) is the source of truth for route
//   gating. It runs BEFORE any React code, reads the `access_token`
//   HttpOnly cookie, and redirects unauthenticated visitors to /login.
//   By the time React mounts on a non-public path we know the cookie
//   was valid at the edge.
//
//   The previous client-side redirect (`router.push('/login')` when
//   `useAuthStore.getState().isAuthenticated` was false) was firing
//   inside a useEffect that ran on every pathname change. Even with
//   the rehydrate-gate added in 7281f61, there was still a race window
//   where Zustand's persist rehydrate completed but the React render
//   cycle hadn't yet propagated the new state — the inner useEffect's
//   `getState()` read the stale initial `isAuthenticated: false`,
//   pushed to /login, and the middleware bounced authenticated cookies
//   from /login to /chat. Net effect: every protected route became /chat.
//
//   Stripping the client redirect entirely:
//     - Eliminates the bounce loop with zero risk of being out-of-sync
//       with the canonical (cookie) auth state.
//     - 401s from any API call are still handled by the axios interceptor
//       in `src/lib/api-client.ts` (silent refresh, then /login?logout=1
//       if refresh fails).
//     - Sidebar still calls `useAuth().initializeAuth()` which validates
//       /auth/me on first mount and force-logs-out if the backend says
//       the session is dead.
//
//   What the AuthProvider still owns:
//     - Async-await rehydrate of the three persisted Zustand stores
//       (auth, chat, notification) before children render — required to
//       prevent the React #418 hydration mismatch AND the blank
//       `tenant_id=` API-call storm caught by the audit.
//     - Rendering `null` while rehydrate is in flight, so neither SSR
//       nor first-paint exposes the empty initial store.

// Routes that render WITHOUT auth and must therefore be server-rendered.
//
// The rehydrate gate below returns `null` on the server, which is correct for
// the app — but it also meant /docs shipped 64 pages whose server HTML was an
// empty shell. Nothing could index them, and link previews showed a blank page.
//
// These prefixes read no auth state and no tenant_id, so none of the failures
// the gate exists to prevent (hydration mismatch, blank-tenant API storms,
// redirect races) can occur on them.
//
// Keep this list minimal and public-only. `/a` (published agent apps) as a
// whole is the obvious next candidate, but it is a separate behavioural change
// and is deliberately NOT included here.
const SSR_PUBLIC_PREFIXES = ['/docs'] as const;

// Two exact routes under `/a`, rather than the `/a` prefix.
//
// These are the pages the published-app confirmation and password-reset emails
// point at. Someone opening one is mid-recovery, usually on a phone, and under
// the rehydrate gate the server sent them an empty shell — measured on the
// deployed build: 19 KB whose only visible text was "Skip to main content".
// Every word waited for the bundle.
//
// They qualify for the same exception /docs does: they read no auth store, no
// tenant_id and no session on render — the app-user token is a separate
// mechanism and is only touched on submit — so the server HTML and the
// client's first paint are identical and no mismatch is possible.
//
// A regex rather than a prefix because the slug is a path segment, and because
// widening this to all of `/a` would change how every published app renders.
// That decision stays where the author left it.
const SSR_PUBLIC_PATTERNS = [/^\/a\/[^/]+\/(reset|verify)\/?$/] as const;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isMounted, setIsMounted] = useState(false);
  const pathname = usePathname();
  const path = pathname || '';
  const isSsrPublic =
    SSR_PUBLIC_PREFIXES.some((p) => path.startsWith(p)) ||
    SSR_PUBLIC_PATTERNS.some((re) => re.test(path));

  useEffect(() => {
    // `persist.rehydrate()` is async — it reads from storage and applies
    // state asynchronously. We must AWAIT all three before flipping
    // `isMounted`, otherwise `AuthProviderInner` mounts while the auth
    // store still has its declared initial state (`isAuthenticated:
    // false`) and bounces every protected route to /login → /chat.
    //
    // The 2026-05-20 public audit (`docs/audits/uzo-public-…/triage-
    // notes.md`, finding #2) caught this: visits to /workspaces,
    // /workflows, /agents, /connectors, etc. all landed on /chat
    // because the inner provider's redirect logic ran before the
    // persist callback resolved.
    // Zustand's `persist.rehydrate()` returns `Promise<void> | void` but
    // doesn't always settle synchronously even when the underlying storage
    // is synchronous — the state mutation happens via the persist
    // middleware's internal `set()`, which schedules. We belt-and-brace
    // by also gating on `hasHydrated()` and forcing a microtask flush.
    let cancelled = false;
    (async () => {
      try {
        await Promise.all([
          useAuthStore.persist.rehydrate(),
          useChatStore.persist.rehydrate(),
          useNotificationStore.persist.rehydrate(),
        ]);
        // Spin until each store reports hasHydrated() OR we hit a
        // safety budget (20 frames ≈ 320ms at 60fps). This catches the
        // race window where rehydrate's promise resolves but the new
        // state isn't yet visible to `useStore.getState()` reads in
        // sibling components that mount immediately after isMounted.
        for (let i = 0; i < 20; i++) {
          const ready =
            useAuthStore.persist.hasHydrated() &&
            useChatStore.persist.hasHydrated() &&
            useNotificationStore.persist.hasHydrated();
          if (ready) break;
          await new Promise((r) => requestAnimationFrame(r));
        }
      } catch (e) {
        // Rehydrate failures are non-fatal — the user just starts from
        // a clean store. Still flip isMounted so children render
        // (otherwise the page hangs blank).
        logger.warn('[AuthProvider] persist rehydrate failed', e);
      }
      if (!cancelled) setIsMounted(true);
    })();
    return () => { cancelled = true; };
  }, []);

  // Gate the entire tree on rehydrate-complete.
  //
  // The earlier version of this provider rendered children eagerly (with
  // `return <>{children}</>`) on both SSR and the first client paint.
  // That meant every page below would mount its `useEffect`s and read
  // `useAuthStore.getState().user?.tenant_id` — which was still the
  // initial `null` because `persist.rehydrate()` hadn't resolved yet.
  // Pages fired their first API calls with `tenant_id=` (blank), the
  // backend ran unindexed queries for nonexistent tenants (2+ seconds),
  // and any page-level effect that depends on auth state could race
  // and redirect (the 2026-05-20 re-audit saw every protected route
  // resolve back to /chat).
  //
  // Returning `null` here is SSR-safe because SSR also runs with
  // `isMounted=false` — the initial server HTML is empty under the
  // ThemeProvider/AuthProvider wrappers, the client hydrates the same
  // empty tree, and only after `useAuthStore.persist.rehydrate()` lands
  // do we render the real children. Brief (~10-50ms) blank flash, but
  // no hydration mismatch and no blank-tenant API storms.
  //
  // EXCEPTION: public documentation routes, and the two published-app auth
  // landing routes, render on the server. They touch no auth state, so the
  // server HTML and the client's first paint are identical and no mismatch is
  // possible.
  if (!isMounted && !isSsrPublic) {
    return null;
  }

  // The guards are slot-stable rather than conditionally prepended.
  //
  // Writing this as `{isMounted && <Guard/>}{children}` keeps `children` at a
  // FIXED child index across the isMounted flip (`false` is a valid child and
  // still occupies its slot). Prepending the guards only after mount would
  // shift children from index 0 to index 2, and React reconciles fragment
  // children positionally — the whole page below would unmount and remount on
  // hydration, re-running every effect and re-firing every fetch.
  return (
    <>
      {isMounted ? <ForcedPasswordChangeGuard /> : null}
      {isMounted ? <BudgetAlertWatcher /> : null}
      {children}
    </>
  );
}
